import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { siteConfigAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Save } from 'lucide-react';
import ContactInfoSection from './settings/ContactInfoSection';
import HeroSection from './settings/HeroSection';
import AboutSection from './settings/AboutSection';

const getStatusClasses = (type) => {
  if (type === 'success') {
    return 'border-green-200 bg-green-50 text-green-800';
  }
  return 'border-red-200 bg-red-50 text-red-800';
};

const AdminSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await siteConfigAPI.get();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatusMessage(null);
    try {
      const savedConfig = await Promise.race([
        siteConfigAPI.update(config),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Save request timed out. Please check your admin permissions and try again.')), 15000);
        }),
      ]);
      setConfig(savedConfig);
      setStatusMessage({
        type: 'success',
        text: 'Settings updated successfully.',
      });
    } catch (error) {
      console.error('Error updating config:', error);
      setStatusMessage({
        type: 'error',
        text: `Failed to update settings: ${error.message || 'Please try again.'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const updateAboutParagraph = (index, value) => {
    const newContent = [...config.about_content];
    newContent[index] = value;
    setConfig({ ...config, about_content: newContent });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64" data-testid="settings-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Settings</h1>

        {statusMessage && (
          <div
            className={`mb-6 rounded-lg border px-4 py-3 text-sm font-medium ${getStatusClasses(statusMessage.type)}`}
            data-testid="settings-status-message"
            role={statusMessage.type === 'error' ? 'alert' : 'status'}
          >
            {statusMessage.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <ContactInfoSection config={config} setConfig={setConfig} />
          <HeroSection config={config} setConfig={setConfig} />
          <AboutSection config={config} onUpdateParagraph={updateAboutParagraph} />

          <Button
            type="submit"
            data-testid="save-settings-btn"
            className="bg-red-700 hover:bg-red-800 flex items-center gap-2"
            disabled={saving}
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
